import { Alert } from '@creditornot/cb-alert';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DecodedValueMap, SetQuery } from 'use-query-params';

import { isAnalyticsFilter } from 'apis/merchant-gateway/merchant-analytics/utils';
import {
  useIsOffersEnabled,
  useSentOffers,
} from 'modules/insights-v2/components/ratings/CompensationOffers/useCompensationOffers';
import { useVenueContext } from 'modules/venue/VenueContext';

import { IsoDateRange } from './FilterDateRangePicker';
import PurchaseDetailsPanel from './PurchaseDetailsPanel';
import PurchaseOrdersTable from './PurchaseOrdersTable';
import usePurchases from './hooks/usePurchases';
import { PurchasesQueryConfig } from './purchasesQueryConfig';

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 24px;
`;

type PurchaseOrdersProps = {
  dateRange: IsoDateRange;
  query: DecodedValueMap<PurchasesQueryConfig>;
  setQuery: SetQuery<PurchasesQueryConfig>;
  showFees: boolean;
};

const PurchaseOrders = ({ dateRange, query, setQuery, showFees }: PurchaseOrdersProps) => {
  const venue = useVenueContext();

  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    status,
  } = usePurchases({
    startDate: dateRange.from,
    endDate: DateTime.fromISO(dateRange.to).plus({ days: 1 }).toISODate(),
    venueId: venue.id,
    ...(query && query.filters && { filters: query.filters.split(',').filter(isAnalyticsFilter) }),
  });

  const purchases = useMemo(() => data.flatMap(entry => entry.purchases), [data]);

  const handleOpenPurchaseDetails = useCallback(
    (purchaseId: string) => {
      setQuery({ purchaseId });
    },
    [setQuery],
  );

  const handleClosePurchaseDetails = useCallback(() => {
    setQuery({ purchaseId: undefined });
  }, [setQuery]);

  // Fetch offer details only for selected purchase, and only if it has a review
  const offerPurchaseIds = useMemo(
    () =>
      query.purchaseId && purchases.some(purchase => purchase.purchaseId === query.purchaseId && purchase.review)
        ? [query.purchaseId]
        : [],
    [query.purchaseId, purchases],
  );
  const { isOffersEnabled } = useIsOffersEnabled(venue);
  const {
    data: sentOffers,
    refetch: refetchSentOffers,
    isFetching: isLoadingOffers,
  } = useSentOffers({
    venueId: venue.id,
    purchaseIds: offerPurchaseIds,
    isEnabled: isOffersEnabled,
  });

  if (status === 'error') {
    return (
      <ErrorAlert>
        <FormattedMessage id="errors.unknown-error-retry-contact" />
      </ErrorAlert>
    );
  }

  return (
    <>
      <PurchaseOrdersTable
        isLoading={status === 'loading'}
        fetchingNextPage={isFetchingNextPage}
        fetchingPreviousPage={isFetchingPreviousPage}
        onClickPurchase={handleOpenPurchaseDetails}
        onFetchNextPage={hasNextPage ? fetchNextPage : undefined}
        onFetchPreviousPage={hasPreviousPage ? fetchPreviousPage : undefined}
        currency={venue.currency}
        purchases={purchases}
        timezone={venue.timezone}
        showFees={showFees}
      />
      {query.purchaseId && (
        <PurchaseDetailsPanel
          onClose={handleClosePurchaseDetails}
          purchaseId={query.purchaseId}
          showFees={showFees}
          venue={venue}
          sentOffer={
            isOffersEnabled ? sentOffers?.find(offer => offer.purchase_id.$oid === query.purchaseId) : undefined
          }
          onSendOffer={isOffersEnabled && !isLoadingOffers ? refetchSentOffers : undefined}
        />
      )}
    </>
  );
};

export default PurchaseOrders;
